"use client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom"; // ✅ from react-router-dom
import { getAllJobs } from "../../../services/jobsServices";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import JobListTable from "../../../components/admin/jobs/JobListTable";
import Pagination from "../../../components/admin/common/Pagination";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";
import { getCookie } from "../../../utils";

const JobsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") || "all";

  const [entriesOption, setEntriesOption] = useState({
    value: 10,
    label: "10",
  });
  const [lastIds, setLastIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [jobsList, setJobsList] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  // Set default status in URL if none exists
  useEffect(() => {
    if (!searchParams.get("status")) {
      setSearchParams({ status: "all" }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const currentLastId =
    currentIndex > 0 ? lastIds[currentIndex - 1] : undefined;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["jobs", currentLastId, entriesOption.value, status],
    queryFn: () =>
      getAllJobs({
        lastId: currentLastId,
        limit: entriesOption.value,
        status: status === "all" ? "" : status,
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.data?.items?.length >= 0) {
      setJobsList(data.data.items);
      setHasMore(data.data.hasMore);
    }
  }, [data]);

  const handleNext = () => {
    if (hasMore && data?.data?.nextCursor) {
      const updated = [...lastIds];
      updated[currentIndex] = data.data.nextCursor;
      setLastIds(updated);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setCurrentIndex(0);
    setLastIds([]);
  };

  // Handle status filter change
  const handleStatusChange = (newStatus) => {
    setSearchParams({ status: newStatus }, { replace: true });
    setCurrentIndex(0);
    setLastIds([]);
  };

  const userId = getCookie("userId");

  const role = getCookie("role");

  const { data: permissionsData } = useUserModulePermissions("Jobs", userId, {
    enabled: role === "maintainer" && userId && userId !== "undefined",
  });

  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <div className="h-full">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4 overflow-y-auto">
              <Header
                title="Job Lists"
                desc="Review, approve, or reject submitted job listings"
              />

              {/* Status Filter Buttons */}
              <div className="mb-4 flex gap-2 mt-4">
                <button
                  onClick={() => handleStatusChange("all")}
                  className={`px-4 py-1 rounded transition-colors ${
                    status === "all"
                      ? "bg-[#4F22D8] text-white shadow-md"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => handleStatusChange("active")}
                  className={`px-4 py-1 rounded transition-colors ${
                    status === "active"
                      ? "bg-green text-white shadow-md"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => handleStatusChange("closed")}
                  className={`px-4 py-1 rounded transition-colors ${
                    status === "closed"
                      ? "bg-red text-white shadow-md"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Closed
                </button>
              </div>

              <DropdownSelect
                selected={entriesOption}
                onChange={handleEntriesChange}
              />

              {isLoading ? (
                <p>Loading jobs...</p>
              ) : isError ? (
                <p>Error fetching jobs</p>
              ) : (
                <>
                  <JobListTable
                    jobs={jobsList}
                    refetch={refetch}
                    startIndex={currentIndex * entriesOption.value}
                    permissionsData={permissionsData?.data ?? null}
                  />
                  <Pagination
                    currentPage={currentIndex + 1}
                    hasMore={hasMore}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                  />
                </>
              )}
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default JobsList;
